import { metrics } from './metrics';
import { webRTC, local_media_stream } from './webRTC';
import { service } from './service';
import { eraseCookie, getCookie, getQueryVariable, getRandomInt } from './helpers/helper';
import { store_api } from './storeAPI';
import { router } from './router';
import { featured_apps } from './featured_apps';
import { firebaseAuth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { transport } from './firebaseTransport';

export class User {
	id: string;
	name: string;
	me: boolean;
	color: string;
	enabled: boolean = false;
	stream: MediaStream = null;
	muted: boolean = true;
	handRaised: boolean = false;
	attentionSignal: 'lost' | 'help' | null = null;
	connectionQuality: 'good' | 'fair' | 'poor' | null = null;
	reactions: { emoji: string; id: string; ts: number }[] = [];

	get showStream() {
		return this.stream && this.stream != state.mainVideoStream;
	}
}

export const store_url = gatheract.apiRoot.replace('/api', ''); //''; // 'https://gatheract.com/';

const colors = [
	'aqua',
	'red',
	'yellow',
	'green',
	'magenta',
	'pink',
	'blue',
	'Maroon',
	'Aquamarine',
	'DarkMagenta',
	'DeepPink',
	'IndianRed',
	'YellowGreen',
	'Cyan',
	'SkyBlue',
	'Goldenrod',
	'SandyBrown',
	'MidnightBlue',
	'Gold',
	'Indigo',
	'Navy',
	'Coral',
	'Tomato',
	'Olive',
	'SpringGreen'
];

export const default_channel_id = 'channel_abc';

export let loginPromise: Promise<IloginResponse> = null;

class State {
	newChatMessage: string = null;
	apiInitPromise = Promise.resolve();

	appsLoadedPromise = Promise.resolve();

	//showJoinDlg = true;
	showShareDlg = false;
	showSupportDlg = false;
	showSettingsDlg = false;
	browsingApps = false;

	connected = false;
	messages: ITextMessage[] = [];
	users: User[] = [];
	user: IUser; // this is the channel user
	appUrl: string = null;
	storeUrl: string = null;
	channelOwner: string | null = null;
	roomLocked: boolean = false;
	videoLayout: 'speaker' | 'gallery' = 'speaker';
	pinnedUserId: string | null = null;
	activeSpeakerId: string | null = null;
	_attentionSignals: Record<string, 'lost' | 'help'> = {};
	micMuted = true;
	audioEnabled = true;
	expanded = true;
	channel_id: string = null;
	app_id: string = null;
	appName: string = null;
	caption_queue: ICaptionPart[] = [];
	captions_enabled: boolean;
	speech_enabled: boolean;

	firstTimeUser: boolean = true;

	appImageUrl = null;
	appList: IAppInfo[] = [];
	appInfo: IAppInfo = null;

	featured_apps: IAppInfo[] = [];

	sharingScreen = false;
	sharingCam = false;
	mainVideoStream: MediaStream = null;

	// luser is the logged in user
	luser: ILocalUser = {
		name: null,
		password: null,
		email: null,
		session_id: null
	};

	gatherActUrl = api.apiRoot.replace('/api', '');
	loadingChannel = false;
	standalone = false;

	get mirrorVideo() {
		return this.sharingCam && this.mainVideoStream == this.users[0].stream;
	}

	get logged_in() {
		return !!this.luser?.session_id;
	}

	get isHost(): boolean {
		return !!this.channelOwner && this.channelOwner === this.user?.id;
	}

	get hasHost(): boolean {
		return !!this.channelOwner;
	}

	get myUser(): User | undefined {
		return this.users.find(u => u.me);
	}

	setUser(r: IloginResponse) {
		if (!r.success) {
			store_api.logout();
			return;
		}

		this.luser = {
			id: undefined,
			email: r.email,
			name: r.name || null,
			session_id: r.session_id,
			is_admin: r.is_admin,
			is_dev: r.is_dev,
			secret: r.secret || null
		};
	}

	toggleSidebar() {
		this.expanded = !this.expanded;
		localStorage.setItem('expanded', `${this.expanded}`);
	}

	stopVideoTrack(vstream: MediaStream) {
		this.users.forEach(u => {
			if (u.stream === vstream) {
				u.stream = null;
			}
		});

		if (this.mainVideoStream === vstream) {
			this.setMainVideo(null);
		}
	}

	addVideoStream(stream: MediaStream, userId: string) {
		let u = state.users.find(u => u.id === userId);
		if (u) u.stream = stream;
		this.setMainVideo(stream);
	}

	setMainVideo(stream: MediaStream) {
		this.mainVideoStream = stream;
	}

	pinUser(userId: string) {
		if (this.pinnedUserId === userId) {
			this.pinnedUserId = null;
			if (this.activeSpeakerId) {
				const speaker = this.users.find(u => u.id === this.activeSpeakerId);
				if (speaker?.stream) this.mainVideoStream = speaker.stream;
			}
		} else {
			this.pinnedUserId = userId;
			const user = this.users.find(u => u.id === userId);
			if (user?.stream) this.mainVideoStream = user.stream;
		}
	}

	setConnectionQuality(userId: string, quality: 'good' | 'fair' | 'poor') {
		const user = this.users.find(u => u.id === userId);
		if (user) user.connectionQuality = quality;
	}

	setActiveSpeaker(userId: string) {
		this.activeSpeakerId = userId;
		if (!this.pinnedUserId) {
			const user = this.users.find(u => u.id === userId);
			if (user?.stream) this.mainVideoStream = user.stream;
		}
	}

	removeQueryFromUrl() {
		let newUrl = document.location.href.replace(document.location.search, '');
		window.history.replaceState(null, document.title, newUrl);
	}

	reset() {
		this.messages.length = 0;
	}

	get hasAudio(): boolean {
		return !!local_media_stream;
	}

	get hasChannel(): boolean {
		return !!this.channel_id?.trim();
	}

	get appLoaded() {
		return !!this.appUrl;
	}

	get showVideo() {
		return !this.browsingApps && this.mainVideoStream;
	}

	get showApp(): boolean {
		if (this.videoLayout === 'gallery') {
			return !this.browsingApps && !!this.appUrl;
		}
		return !this.browsingApps && !!this.appUrl && !this.mainVideoStream;
	}

	get canUseGallery(): boolean {
		return !this.appUrl && !this.sharingScreen;
	}

	setVideoLayout(layout: 'speaker' | 'gallery') {
		if (layout === 'gallery' && !this.canUseGallery) {
			this.videoLayout = 'speaker';
			return;
		}
		this.videoLayout = layout;
	}

	get messageThreads(): IThread[] {
		const result = [];
		let lastThread = null;
		this.messages.forEach(message => {
			if (!lastThread || lastThread.from !== message.from) {
				lastThread = {
					from: message.from,
					messages: [message]
				};
				result.push(lastThread);
			} else {
				lastThread.messages.push(message);
			}
		});
		return result;
	}

	async init() {
		// Restore login via Firebase auth state (previous REST /login by session is gone).
		onAuthStateChanged(firebaseAuth, async u => {
			if (u && !u.isAnonymous) {
				try {
					const tokenResult = await u.getIdTokenResult();
					if (tokenResult.claims.mustResetPassword === true) {
						// Don't restore the session — force the user through /login
						// where they'll be prompted to set a new password.
						if (router.currentRoute.value.path !== '/login') {
							router.push('/login');
						}
						return;
					}
					this.setUser({
						success: true,
						email: u.email || undefined,
						name: u.displayName || undefined,
						session_id: tokenResult.token,
						id: u.uid,
						is_admin: tokenResult.claims.is_admin === true,
						is_dev: tokenResult.claims.is_dev === true
					});
				} catch (e) {
					console.error('auth restore failed', e);
				}
			}
		});

		store_api.getApps({}).then(r => {
			this.appList = r;
			this.featured_apps = featured_apps;
		});

		if (document.location.search) {
			this.app_id = getQueryVariable('app');
			this.luser.session_id = getQueryVariable('session');
		}

		let expand = JSON.parse(localStorage.getItem('expanded'));
		if (expand !== null) {
			this.expanded = expand;
		}
	}

	async hangup(opts?: { navigate?: boolean }) {
		metrics.send();
		api.leaveChannel();
		webRTC.closeAllConnections();

		this.micMuted = true;
		this.storeUrl = null;
		this.channel_id = null;
		this.users = [];
		this.messages = [];
		this.appUrl = null;
		this.sharingScreen = false;
		this.sharingCam = false;
		this.appName = null;
		this.channelOwner = null;
		this.roomLocked = false;
		this.videoLayout = 'speaker';
		this.pinnedUserId = null;
		this.activeSpeakerId = null;
		this.mainVideoStream = null;
		this._attentionSignals = {};

		this.browsingApps = false;
		//this.showJoinDlg = true;
		this.showShareDlg = false;
		this.showSupportDlg = false;
		this.showSettingsDlg = false;

		if (opts?.navigate !== false) {
			router.push('/');
		}
	}

	handleUserBanned(channel_id: string, banned_user_name: string) {
		// Show notification
		alert(`You have been banned from ${channel_id}`);
		// Disconnect from the channel
		this.hangup();
	}

	handleKickedFromChannel() {
		// Server-side kick (e.g. /ban_user). The user_banned broadcast handler
		// shows its own alert; if it didn't fire (race / network), make sure
		// we still drop the local session.
		if (this.channel_id) {
			this.hangup();
		}
	}

	closeApp(sendMessage = true) {
		metrics.endApp();
		this.appUrl = '';
		this.appName = '';
		if (sendMessage) {
			transport
				.broadcast({ type: 'close_app' })
				.catch(err => console.error('close_app broadcast failed', err));
		}
	}

	enableMic(enabled: boolean) {
		this.micMuted = !enabled;
		webRTC.enableMic(enabled);
		// Reflect locally immediately so our own row updates without waiting for the
		// echoed broadcast (and in case includeSelf-style filtering is added later).
		this.updateUserMutedState(this.user, this.micMuted);
		transport
			.broadcast({ type: 'muted', muted: this.micMuted, user: this.user })
			.catch(err => console.error('mute broadcast failed', err));
	}

	enableCam() {
		webRTC.enableCam();
	}

	shareScreen() {
		webRTC.screenShare();
	}

	browseApps() {
		/*if (!this.storeUrl) {
			this.storeUrl = gatheract.storeUrl;
		}*/
		this.browsingApps = true;
	}

	stopBrowsingApps() {
		this.browsingApps = false;
	}

	getColor(): string {
		let result = colors.find(c => !this.users.some(u => u.color === c));
		return result || colors[getRandomInt(0, colors.length - 1)];
	}

	updateUserMutedState(user: IUser, muted: boolean) {
		let foundUser = this.users.find(ur => ur.id === user.id);
		if (foundUser) {
			Vue.nextTick(() => {
				foundUser.muted = muted;
			});
		}
	}

	updateUsers(users: IUser[]) {
		let used = {};
		users.forEach(u => {
			used[u.id] = true;
			let user = this.users.find(ur => ur.id === u.id);
			if (!user) {
				user = new User();
				user.id = u.id;
				user.color = this.getColor();
				user.me = u.id === state.user.id;

				if (user.me) {
					this.users.unshift(user);
				} else {
					this.users.push(user);
				}
			}
			user.name = u.name;
			user.attentionSignal = this._attentionSignals[u.id] ?? null;
		});

		metrics.trackUsers(users.map(u => ({ id: u.id, name: u.name })));

		for (let i = this.users.length - 1; i >= 0; i--) {
			if (used[this.users[i].id] === undefined) {
				this.users.splice(i, 1);
			}
		}
	}
}

export const state = Vue.reactive(new State());
state.init();
//@ts-ignore
window.state = state;
